import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Calculator, Phone, MapPin, Mail, User, CheckCircle, Clock, Home, Loader2, Building2, Ruler, Calendar, PenTool as Tool } from 'lucide-react';
import { sendProjectPlannerEmail } from '../lib/emailjs';
import { getProjectEstimate } from '../lib/openai';
import { trackEvent } from '../lib/analytics';

interface ProjectDetails {
  service: string;
  description: string;
  hasMaterials: boolean;
  projectSize: string;
  timeline: string;
  propertyType: string;
  address: string;
  name: string;
  email: string;
  phone: string;
}

const services = [
  'Kitchen Renovation',
  'Bathroom Remodeling',
  'Home Addition',
  'Basement Finishing',
  'Custom Carpentry',
  'Deck & Patio',
  'General Repairs',
  'Other (Please Specify)'
];

const propertyTypes = [
  'Single Family Home',
  'Townhouse',
  'Condo/Apartment',
  'Multi-Family Home'
];

const projectSizes = [
  'Small (Single Room/Area)',
  'Medium (Multiple Rooms)',
  'Large (Whole Floor/House)'
];

const timelines = [
  'As Soon as Possible',
  'Within 1-2 Months',
  'Within 3-6 Months',
  'More than 6 Months',
  'Just Planning'
];

const calculateEstimate = (details: ProjectDetails) => {
  let basePrice = 0;
  let multiplier = 1;

  // Base price by service type
  switch (details.service) {
    case 'Kitchen Renovation':
      basePrice = 25000;
      break;
    case 'Bathroom Remodeling':
      basePrice = 15000;
      break;
    case 'Home Addition':
      basePrice = 50000;
      break;
    case 'Basement Finishing':
      basePrice = 30000;
      break;
    case 'Custom Carpentry':
      basePrice = 8000;
      break;
    case 'Deck & Patio':
      basePrice = 12000;
      break;
    case 'General Repairs':
      basePrice = 3000;
      break;
    default:
      basePrice = 10000;
  }

  // Adjust for project size
  switch (details.projectSize) {
    case 'Small (Single Room/Area)':
      multiplier *= 0.8;
      break;
    case 'Medium (Multiple Rooms)':
      multiplier *= 1.2;
      break;
    case 'Large (Whole Floor/House)':
      multiplier *= 1.5;
      break;
  }

  // Adjust for materials
  if (details.hasMaterials) {
    multiplier *= 0.6; // Reduce price if client has materials
  }

  // Apply multiplier
  basePrice *= multiplier;

  // Create range
  const minPrice = Math.round(basePrice * 0.8);
  const maxPrice = Math.round(basePrice * 1.2);

  return { minPrice, maxPrice };
};

export function OnlineProjectPlanner() {
  const [step, setStep] = useState(1);
  const [projectDetails, setProjectDetails] = useState<ProjectDetails>({
    service: '',
    description: '',
    hasMaterials: false,
    projectSize: '',
    timeline: '',
    propertyType: '',
    address: '',
    name: '',
    email: '',
    phone: ''
  });
  const [estimate, setEstimate] = useState<{ minPrice: number; maxPrice: number } | null>(null);
  const [isGeneratingEstimate, setIsGeneratingEstimate] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [estimateProgress, setEstimateProgress] = useState(0);

  const handleNext = async () => {
    if (step === 4) {
      setIsGeneratingEstimate(true);
      setEstimateProgress(0);
      
      // Start progress animation
      const progressInterval = setInterval(() => {
        setEstimateProgress(prev => {
          if (prev >= 90) return 90;
          return prev + 10;
        });
      }, 500);

      try {
        const aiEstimate = await getProjectEstimate({
          service: projectDetails.service,
          description: projectDetails.description,
          hasMaterials: projectDetails.hasMaterials,
          projectSize: projectDetails.projectSize,
          timeline: projectDetails.timeline,
          propertyType: projectDetails.propertyType
        });
        
        setEstimateProgress(100);
        const finalEstimate = {
          minPrice: Math.round(aiEstimate.totalCost * 0.9),
          maxPrice: Math.round(aiEstimate.totalCost * 1.1)
        };
        setEstimate(finalEstimate);

        // Automatically send email after estimate generation
        await sendProjectPlannerEmail({
          ...projectDetails,
          estimatedCost: `$${finalEstimate.minPrice.toLocaleString()} - $${finalEstimate.maxPrice.toLocaleString()}`
        });

      } catch (error) {
        console.error('Error getting estimate:', error);
        const fallbackEstimate = calculateEstimate(projectDetails);
        setEstimate(fallbackEstimate);
        
        // Still try to send email with fallback estimate
        await sendProjectPlannerEmail({
          ...projectDetails,
          estimatedCost: `$${fallbackEstimate.minPrice.toLocaleString()} - $${fallbackEstimate.maxPrice.toLocaleString()}`
        });
      } finally {
        clearInterval(progressInterval);
        setIsGeneratingEstimate(false);
        setStep(step + 1);
      }
    } else {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await sendProjectPlannerEmail({
        name: projectDetails.name,
        email: projectDetails.email,
        phone: projectDetails.phone,
        service: projectDetails.service,
        description: projectDetails.description,
        propertyType: projectDetails.propertyType,
        projectSize: projectDetails.projectSize,
        timeline: projectDetails.timeline,
        hasMaterials: projectDetails.hasMaterials,
        address: projectDetails.address,
        estimatedCost: estimate ? `$${estimate.minPrice.toLocaleString()} - $${estimate.maxPrice.toLocaleString()}` : 'TBD'
      });
      setIsComplete(true);
      trackEvent('generate_lead', {
        method: 'project_planner',
        page_path: window.location.pathname,
      });
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isStepValid = () => {
    switch (step) {
      case 1:
        return projectDetails.service !== '' && projectDetails.description !== '';
      case 2:
        return projectDetails.propertyType !== '' && projectDetails.projectSize !== '' && projectDetails.timeline !== '';
      case 3:
        return projectDetails.address !== '';
      case 4:
        return projectDetails.name !== '' && projectDetails.email !== '' && projectDetails.phone !== '';
      default:
        return false;
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {!isComplete ? (
        <div className="bg-zinc-800/30 rounded-xl p-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-xl text-zinc-100">Online Project Planner</h2>
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className={`h-2 w-2 rounded-full ${
                    i === step ? 'bg-yellow-400' : i < step ? 'bg-yellow-400/50' : 'bg-zinc-700'
                  }`}
                />
              ))}
            </div>
          </div>

          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">What service are you interested in?</label>
                  <select
                    value={projectDetails.service}
                    onChange={(e) => setProjectDetails({ ...projectDetails, service: e.target.value })}
                    className="w-full bg-zinc-700/50 border border-zinc-600 rounded-lg px-4 py-3 text-zinc-200"
                  >
                    <option value="">Select a service</option>
                    {services.map((service) => (
                      <option key={service} value={service}>
                        {service}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Describe your project</label>
                  <textarea
                    value={projectDetails.description}
                    onChange={(e) => setProjectDetails({ ...projectDetails, description: e.target.value })}
                    className="w-full bg-zinc-700/50 border border-zinc-600 rounded-lg px-4 py-3 text-zinc-200 h-32"
                    placeholder="Please provide details about your project..."
                  />
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Property Type</label>
                  <select
                    value={projectDetails.propertyType}
                    onChange={(e) => setProjectDetails({ ...projectDetails, propertyType: e.target.value })}
                    className="w-full bg-zinc-700/50 border border-zinc-600 rounded-lg px-4 py-3 text-zinc-200"
                  >
                    <option value="">Select property type</option>
                    {propertyTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Project Size</label>
                  <select
                    value={projectDetails.projectSize}
                    onChange={(e) => setProjectDetails({ ...projectDetails, projectSize: e.target.value })}
                    className="w-full bg-zinc-700/50 border border-zinc-600 rounded-lg px-4 py-3 text-zinc-200"
                  >
                    <option value="">Select project size</option>
                    {projectSizes.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Desired Timeline</label>
                  <select
                    value={projectDetails.timeline}
                    onChange={(e) => setProjectDetails({ ...projectDetails, timeline: e.target.value })}
                    className="w-full bg-zinc-700/50 border border-zinc-600 rounded-lg px-4 py-3 text-zinc-200"
                  >
                    <option value="">Select timeline</option>
                    {timelines.map((timeline) => (
                      <option key={timeline} value={timeline}>
                        {timeline}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-zinc-400 mb-4">Do you have your own materials?</label>
                  <div className="flex gap-4">
                    <button
                      onClick={() => setProjectDetails({ ...projectDetails, hasMaterials: true })}
                      className={`flex-1 px-4 py-3 rounded-lg border ${
                        projectDetails.hasMaterials
                          ? 'bg-yellow-400 text-zinc-900 border-yellow-400'
                          : 'border-zinc-600 text-zinc-400'
                      }`}
                    >
                      Yes
                    </button>
                    <button
                      onClick={() => setProjectDetails({ ...projectDetails, hasMaterials: false })}
                      className={`flex-1 px-4 py-3 rounded-lg border ${
                        !projectDetails.hasMaterials
                          ? 'bg-yellow-400 text-zinc-900 border-yellow-400'
                          : 'border-zinc-600 text-zinc-400'
                      }`}
                    >
                      No
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">What is the project location?</label>
                  <div className="relative">
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400" />
                    <input
                      type="text"
                      value={projectDetails.address}
                      onChange={(e) => setProjectDetails({ ...projectDetails, address: e.target.value })}
                      className="w-full bg-zinc-700/50 border border-zinc-600 rounded-lg pl-12 pr-4 py-3 text-zinc-200"
                      placeholder="Enter your address"
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {step === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Your Name</label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400" />
                    <input
                      type="text"
                      value={projectDetails.name}
                      onChange={(e) => setProjectDetails({ ...projectDetails, name: e.target.value })}
                      className="w-full bg-zinc-700/50 border border-zinc-600 rounded-lg pl-12 pr-4 py-3 text-zinc-200"
                      placeholder="Enter your name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400" />
                    <input
                      type="email"
                      value={projectDetails.email}
                      onChange={(e) => setProjectDetails({ ...projectDetails, email: e.target.value })}
                      className="w-full bg-zinc-700/50 border border-zinc-600 rounded-lg pl-12 pr-4 py-3 text-zinc-200"
                      placeholder="Enter your email"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400" />
                    <input
                      type="tel"
                      value={projectDetails.phone}
                      onChange={(e) => setProjectDetails({ ...projectDetails, phone: e.target.value })}
                      className="w-full bg-zinc-700/50 border border-zinc-600 rounded-lg pl-12 pr-4 py-3 text-zinc-200"
                      placeholder="Enter your phone number"
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {step === 5 && estimate && (
              <motion.div
                key="step5"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-8"
              >
                <div className="bg-yellow-400/10 border border-yellow-400/20 rounded-lg p-6 text-center">
                  <Calculator className="h-8 w-8 text-yellow-400 mx-auto mb-4" />
                  <h3 className="text-xl text-zinc-100 mb-2">AI-Generated Project Estimate</h3>
                  <p className="text-3xl text-yellow-400 font-light">
                    ${estimate.minPrice.toLocaleString()} - ${estimate.maxPrice.toLocaleString()}
                  </p>
                  <p className="text-sm text-zinc-400 mt-4">
                    This is an AI-generated estimate based on average costs in your area.
                    For the most accurate pricing, call us directly.
                  </p>
                </div>

                <div className="space-y-4">
                  <h4 className="text-zinc-200">Project Summary</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-zinc-400">
                      <Home className="h-4 w-4" />
                      <span>Service: <span className="text-zinc-200">{projectDetails.service}</span></span>
                    </div>
                    <div className="flex items-center gap-2 text-zinc-400">
                      <MapPin className="h-4 w-4" />
                      <span>Location: <span className="text-zinc-200">{projectDetails.address}</span></span>
                    </div>
                    <div className="flex items-center gap-2 text-zinc-400">
                      <Clock className="h-4 w-4" />
                      <span>Timeline: <span className="text-zinc-200">{projectDetails.timeline}</span></span>
                    </div>
                  </div>
                </div>

                <div className="text-center">
                  <a
                    href="tel:9149606433"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-yellow-400 text-zinc-900 rounded-lg hover:bg-yellow-500 transition-colors"
                  >
                    <Phone className="h-5 w-5" />
                    Call (914) 960-6433 for Accurate Quote
                  </a>
                </div>
              </motion.div>
            )}

            {isGeneratingEstimate && (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center py-12 space-y-6"
              >
                <div className="relative mx-auto w-16 h-16">
                  <Loader2 className="w-16 h-16 text-yellow-400 animate-spin" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-sm font-medium text-yellow-400">{estimateProgress}%</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl text-zinc-100">Generating Your Estimate</h3>
                  <p className="text-zinc-400">Our AI is analyzing your project details...</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex items-center justify-between mt-8">
            {step > 1 && !isGeneratingEstimate && (
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-zinc-400 hover:text-yellow-400 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
            )}
            {step < 5 && !isGeneratingEstimate && (
              <button
                onClick={handleNext}
                disabled={!isStepValid()}
                className="ml-auto flex items-center gap-2 px-6 py-3 bg-yellow-400 text-zinc-900 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      ) : (
        <motion.div
          key="complete"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-zinc-800/30 rounded-xl p-8 text-center"
        >
          <CheckCircle className="h-12 w-12 text-yellow-400 mx-auto mb-4" />
          <h3 className="text-xl text-zinc-100 mb-4">Thank You!</h3>
          <p className="text-zinc-400 mb-6">
            We've received your project details and will contact you shortly with a detailed quote.
          </p>
          <div className="flex items-center justify-center gap-4">
            <a
              href="tel:9144733775"
              className="flex items-center gap-2 text-yellow-400 hover:text-yellow-300 transition-colors"
            >
              <Phone className="h-5 w-5" />
              (914) 473-3775
            </a>
          </div>
        </motion.div>
      )}
    </div>
  );
}